import React, { useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import { useDispatch, useSelector } from 'react-redux';
import UserSidebar from './UserSidebar';
import '../Admin.css';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Table,
  Badge,
  Button,
  Input,
  InputGroup,
  InputGroupText,
  Pagination,
  PaginationItem,
  PaginationLink,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  FormFeedback,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Progress,
  Alert
} from 'reactstrap';
import ExportButton from '../../../components/ExportButton';
import BulkActions from '../../../components/BulkActions';
import {
  getAdminUsers,
  getAdminUserStats,
  updateAdminUser,
  impersonateUser,
  createAdminUser,
  getUserActivities,
  exportAdminUsers,
  deleteAdminUser,
  getAvailableWorkspaces
} from '../../../store/Admin';
import { TextField, Checkbox } from '@mui/material';
import FormHelperText from '@mui/material/FormHelperText';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import { padding } from '../../../../node_modules/@mui/system/index';
const UsersManagement = ({ accessType, workspaceId, isTabMode = false }) => {
  const dispatch = useDispatch();
  const { adminUsers, adminUserStats, usersPagination, loading, activityLogs, availableWorkspaces } = useSelector(state => state['Admin']);
  const { user } = useSelector(state => state['Auth'] || {});

  // Debug logging
  console.log('UsersManagement props:', { accessType, workspaceId, isTabMode });
  console.log('User from Auth:', user);

  // Function to fetch available workspaces
  const fetchAvailableWorkspaces = async () => {
    // Try to fetch workspaces - backend will determine if user has access
    setWorkspacesLoading(true);
    try {
      await dispatch(getAvailableWorkspaces());
    } catch (error) {
      console.error('Error fetching workspaces:', error);
    } finally {
      setWorkspacesLoading(false);
    }
  };

  // Function to handle create user modal opening
  const handleCreateUserModalOpen = () => {
    setShowCreateModal(true);
    // Always try to fetch workspaces - the backend will handle permissions
    fetchAvailableWorkspaces();
  };

  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [newUser, setNewUser] = useState({ 'firstName': '', 'lastName': '', 'email': '', 'password': '', 'role': 'user', 'status': 'active', 'workspaceOption': 'new', 'existingWorkspaceId': '' });
  const [createErrors, setCreateErrors] = useState({});
  const [exportLoading, setExportLoading] = useState(false);
  const [workspacesLoading, setWorkspacesLoading] = useState(false);

  // Modal states for action confirmations
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalConfig, setConfirmModalConfig] = useState({
    title: '',
    message: '',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    confirmColor: 'primary',
    icon: 'fa-question-circle',
    iconColor: '#2563EB',
    onConfirm: null,
    showWarning: false,
    warningMessage: ''
  });

  // Success/Info modal states
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [infoModalConfig, setInfoModalConfig] = useState({
    title: '',
    message: '',
    type: 'success', // success, info, warning, error
    icon: 'fa-check-circle',
    iconColor: '#16A34A'
  });

  useEffect(() => {
    dispatch(getAdminUsers({ page: currentPage, search: searchTerm, status: statusFilter }));
    dispatch(getAdminUserStats());
  }, [currentPage, searchTerm, statusFilter]);

  // Helpers to mirror Dashboard recent activity visuals
  const getActivityIcon = (type) => {
    const icons = { 'user_created': 'fa-user-plus', 'user_updated': 'fa-user-edit', 'user_deleted': 'fa-user-times', 'workspace_created': 'fa-building', 'workspace_updated': 'fa-edit', 'workspace_suspended': 'fa-pause-circle', 'workspace_deleted': 'fa-trash-alt', 'agent_created': 'fa-robot', 'agent_started': 'fa-play-circle', 'agent_stopped': 'fa-stop-circle', 'agent_deleted': 'fa-trash-alt', 'system_backup': 'fa-database', 'system_update': 'fa-sync-alt', 'system_error': 'fa-exclamation-triangle', 'login_success': 'fa-sign-in-alt', 'login_failed': 'fa-times-circle', 'subscription_created': 'fa-credit-card', 'subscription_upgraded': 'fa-arrow-up', 'subscription_cancelled': 'fa-times', 'payment_success': 'fa-dollar-sign', 'payment_failed': 'fa-exclamation-circle', 'security_alert': 'fa-shield-alt', 'api_key_created': 'fa-key', 'api_key_revoked': 'fa-ban' };
    return icons[type] || 'fa-info-circle';
  };

  const getActivityColor = (severity) => {
    const colors = { 'success': 'text-success', 'info': 'text-info', 'warning': 'text-warning', 'error': 'text-danger', 'critical': 'text-danger' };
    return colors[severity] || 'text-secondary';
  };

  const getRelativeTime = (timestamp) => {
    const diffMs = Date.now() - new Date(timestamp).getTime();
    const sec = Math.floor(diffMs / 1000);
    if (sec < 60) return `${sec}s ago`;
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const day = Math.floor(hr / 24);
    return `${day}d ago`;
  };

  // Fetch user-specific activities when opening preview
  useEffect(() => {
    if (showModal && selectedUser?._id) {
      dispatch(getUserActivities({ userId: selectedUser['_id'], limit: 10 }));
    }
  }, [showModal, selectedUser]);

  const handleUserAction = (userId, action) => {
    if (action === 'activate' || action === 'deactivate' || action === 'suspend' || action === 'restore') {
      const user = adminUsers.find(u => u._id === userId);
      if (!user) return;

      if (action === 'suspend') {
        // Show confirmation modal for pause
        setConfirmModalConfig({
          title: 'Pause User',
          message: `Are you sure you want to pause ${user.firstName} ${user.lastName}?`,
          confirmText: 'Yes, Pause User',
          cancelText: 'Cancel',
          confirmColor: 'warning',
          icon: 'fa-pause-circle',
          iconColor: '#FF8B00',
          showWarning: true,
          warningMessage: 'This will:\n• Temporarily prevent them from logging in\n• Maintain their workspace access once reactivated\n• Be recorded in audit logs\n\nYou can reactivate them later if needed.',
          onConfirm: () => {
            setShowConfirmModal(false);
            dispatch({
              type: 'DEACTIVATE_USER',
              payload: {
                id: userId,
                action: 'suspend',
                filters: { page: currentPage, search: searchTerm, status: statusFilter }
              }
            });
          }
        });
        setShowConfirmModal(true);
      } else if (action === 'deactivate') {
        // Show confirmation modal for deactivate
        setConfirmModalConfig({
          title: 'Deactivate User',
          message: `Are you sure you want to deactivate ${user.firstName} ${user.lastName}?`,
          confirmText: 'Yes, Deactivate User',
          cancelText: 'Cancel',
          confirmColor: 'danger',
          icon: 'fa-user-slash',
          iconColor: '#EF4D56',
          showWarning: true,
          warningMessage: 'This will:\n• Prevent them from logging in\n• Disable access to all workspaces\n• Be recorded in audit logs\n\nYou can restore them later if needed.',
          onConfirm: () => {
            setShowConfirmModal(false);
            dispatch({
              type: 'DEACTIVATE_USER',
              payload: {
                id: userId,
                action: 'deactivate',
                filters: { page: currentPage, search: searchTerm, status: statusFilter }
              }
            });
          }
        });
        setShowConfirmModal(true);
      } else if (action === 'restore') {
        // Restore deactivated user
        const actionType = 'RESTORE_USER';
        dispatch({
          type: actionType,
          payload: {
            id: userId,
            filters: { page: currentPage, search: searchTerm, status: statusFilter }
          }
        });
      } else {
        // Direct activation without confirmation
        const actionType = 'ACTIVATE_USER';
        dispatch({
          type: actionType,
          payload: {
            id: userId,
            filters: { page: currentPage, search: searchTerm, status: statusFilter }
          }
        });
      }
    }
    if (action === 'impersonate') {
      return handleImpersonateUser(userId);
    }
    if (action === 'delete' && accessType === 'agency') {
      return handleDeleteUser(userId);
    }
  };

  const handleDeleteUser = (userId) => {
    const user = adminUsers.find(u => u._id === userId);
    if (!user) return;

    // Show confirmation modal for deactivate
    setConfirmModalConfig({
      title: 'Deactivate User',
      message: `Are you sure you want to deactivate ${user.firstName} ${user.lastName}?`,
      confirmText: 'Yes, Deactivate User',
      cancelText: 'Cancel',
      confirmColor: 'danger',
      icon: 'fa-user-slash',
      iconColor: '#EF4D56',
      showWarning: true,
      warningMessage: 'This will:\n• Remove them from all workspaces\n• Prevent them from logging in\n• Be recorded in audit logs\n\nThe user can be restored later.',
      onConfirm: () => {
        setShowConfirmModal(false);
        dispatch(deleteAdminUser({
          id: userId,
          filters: { page: currentPage, search: searchTerm, status: statusFilter }
        }));
      }
    });
    setShowConfirmModal(true);
  };

  const handleImpersonateUser = (userId) => {
    const user = adminUsers.find(u => u._id === userId);
    if (!user) return;

    // Show confirmation modal for impersonate
    setConfirmModalConfig({
      title: 'Security Warning',
      message: `You are about to impersonate ${user.firstName} ${user.lastName} (${user.email}).`,
      confirmText: 'Continue with Impersonation',
      cancelText: 'Cancel',
      confirmColor: 'warning',
      icon: 'fa-user-secret',
      iconColor: '#F59E0B',
      showWarning: true,
      warningMessage: 'This action will:\n• Log you in as this user for 2 hours\n• Be recorded in audit logs\n• Give you access to their workspace and data\n\nDo you want to continue?',
      onConfirm: () => {
        setShowConfirmModal(false);
        dispatch(impersonateUser({
          'id': userId,
          'callback': (data) => {
            if (data && data.token) {
              // Store current admin context
              const currentToken = localStorage.getItem('token');
              const adminContext = {
                originalToken: currentToken,
                adminId: data.user._id,
                adminName: `Admin impersonating ${user.firstName} ${user.lastName}`,
                impersonatedAt: new Date().toISOString(),
                expiresAt: new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString()
              };

              localStorage.setItem('impersonationContext', JSON.stringify(adminContext));
              localStorage.setItem('token', data.token);

              // Show success message
              setInfoModalConfig({
                title: 'Impersonation Successful',
                message: `Successfully impersonating ${user.firstName} ${user.lastName}.\n\nYou will now be redirected to their dashboard.\nSession expires in 2 hours.`,
                type: 'success',
                icon: 'fa-check-circle',
                iconColor: '#16A34A'
              });
              setShowInfoModal(true);

              // For demo purposes, show what would happen
              console.log('Impersonation Context:', adminContext);
              console.log('New Token:', data.token);

              // In production, redirect to main application dashboard
              // window.location.href = '/dashboard';

              // For demo, show additional info after a delay
              setTimeout(() => {
                setInfoModalConfig({
                  title: '🎭 Demo Mode',
                  message: 'In production, you would now be logged in as the user.\n\nFeatures:\n• Full access to their workspaces\n• See their exact UI/permissions\n• Perform actions as them\n• 2-hour session timeout\n• All actions logged for audit',
                  type: 'info',
                  icon: 'fa-info-circle',
                  iconColor: '#2563EB'
                });
                setShowInfoModal(true);
                setTimeout(() => setShowInfoModal(false), 5000);
              }, 3000);
            }
          }
        }));
      }
    });
    setShowConfirmModal(true);
  };

  const handleCreateUser = () => {
    // Reset errors
    setCreateErrors({});

    // Validate form fields
    const errors = {};

    if (!newUser.firstName || newUser.firstName.trim() === '') {
      errors.firstName = 'First name is required';
    }

    if (!newUser.lastName || newUser.lastName.trim() === '') {
      errors.lastName = 'Last name is required';
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!newUser.email || newUser.email.trim() === '') {
      errors.email = 'Email is required';
    } else if (!emailRegex.test(newUser.email)) {
      errors.email = 'Please enter a valid email address';
    }

    // Validate password length (only for new workspace creation)
    if (newUser.workspaceOption === 'new') {
      if (!newUser.password || newUser.password === '') {
        errors.password = 'Password is required';
      } else if (newUser.password.length < 6) {
        errors.password = 'Password must be at least 6 characters long';
      }
    }

    // Validate workspace selection when using existing workspace option
    if (newUser.workspaceOption === 'existing') {
      if (!newUser.existingWorkspaceId || newUser.existingWorkspaceId === '') {
        errors.existingWorkspaceId = 'Please select a workspace';
      }
    }

    // If there are errors, show them and don't proceed
    if (Object.keys(errors).length > 0) {
      setCreateErrors(errors);
      return;
    }

    // Dispatch the action to create user
    const userData = {
      ...newUser,
      firstName: newUser.firstName.trim(),
      lastName: newUser.lastName.trim(),
      email: newUser.email.trim().toLowerCase()
    };

    // If adding to existing workspace, include the workspace role
    if (newUser.workspaceOption === 'existing') {
      userData.role = newUser.workspaceRole; // Use workspaceRole for member assignment
    }

    dispatch(createAdminUser({
      'userData': userData,
      'filters': { 'page': currentPage, 'search': searchTerm, 'status': statusFilter }
    }));

    // Close modal and reset form
    setShowCreateModal(false);
    setNewUser({ 'firstName': '', 'lastName': '', 'email': '', 'password': '', 'role': 'user', 'status': 'active', 'workspaceOption': 'new', 'existingWorkspaceId': '' });
    setCreateErrors({});
  };

  const handleBulkAction = async (action, userIds, data = {}) => {
    const filters = { 'page': currentPage, 'search': searchTerm, 'status': statusFilter };

    switch (action) {
      case 'activate':
        return dispatch({
          'type': 'BULK_USER_ACTION',
          'payload': {
            'action': 'activate',
            'userIds': userIds,
            'filters': filters
          }
        });
      case 'suspend':
        return dispatch({
          'type': 'BULK_USER_ACTION',
          'payload': {
            'action': 'suspend',
            'userIds': userIds,
            'filters': filters
          }
        });
      case 'deactivate':
        return dispatch({
          'type': 'BULK_USER_ACTION',
          'payload': {
            'action': 'deactivate',
            'userIds': userIds,
            'filters': filters
          }
        });
      case 'delete':
        return dispatch({
          'type': 'DELETE_BULK_USERS',
          'payload': {
            'userIds': userIds,
            'filters': filters
          }
        });
      case 'change-role':
        return dispatch({
          'type': 'BULK_USER_ACTION',
          'payload': {
            'action': 'update',
            'userIds': userIds,
            'data': { 'role': data['role'] },
            'filters': filters
          }
        });
      default:
        console.warn('Unknown bulk action:', action);
    }

    // Clear selection after action
    setSelectedUsers([]);
    setSelectAll(false);
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(adminUsers.map(user => user['_id']));
    }
    setSelectAll(!selectAll);
  };

  const handleDeselectAll = () => {
    setSelectedUsers([]);
    setSelectAll(false);
  };

  const handleUserSelect = (userId) => {
    setSelectedUsers(prev => {
      const newSelected = prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId];

      setSelectAll(newSelected.length === adminUsers.length);
      return newSelected;
    });
  };

  const allBulkActions = [
    {
      'key': 'activate', 'type': 'activate', 'label': 'Activate', 'description': 'Activate selected users', 'requiresConfirmation': true, 'confirmationMessage': 'This will activate all selected users.'
    },
    {
      'key': 'suspend', 'type': 'suspend', 'label': 'Pause', 'description': 'Pause selected users', 'requiresConfirmation': true, 'confirmationMessage': 'This will pause all selected users.', 'warning': 'Paused users will not be able to login.'
    },
    {
      'key': 'deactivate', 'type': 'deactivate', 'label': 'Deactivate', 'description': 'Deactivate selected users', 'requiresConfirmation': true, 'confirmationMessage': 'This will deactivate all selected users.', 'warning': 'Deactivated users will not be able to login.'
    },
    {
      'key': 'change-role', 'type': 'edit', 'label': 'Change Role', 'description': 'Change role for selected users', 'requiresConfirmation': true, 'showInAgency': false, 'fields': [
        {
          'key': 'role', 'label': 'New Role', 'type': 'select', 'required': true, 'options': [{ 'value': 'user', 'label': 'User' }, { 'value': 'admin', 'label': 'Admin' }]
        }
      ]
    },
    {
      'key': 'delete', 'type': 'delete', 'label': 'Delete Users', 'description': 'Permanently delete selected users', 'requiresConfirmation': true, 'confirmationMessage': 'This will permanently delete all selected users and their data.', 'warning': 'This action cannot be undone. All user data will be permanently deleted.', 'withProgress': true
    }
  ];

  const bulkActions = accessType === 'agency'
    ? allBulkActions.filter(action => action.showInAgency !== false)
    : allBulkActions;

  const getUserStatusBadge = (status) => {
    // Default to 'active' if status is not defined (for existing users)
    const userStatus = status || 'active';

    const statusConfig = {
      'active': { color: 'success', text: 'Active' },
      'inactive': { color: 'danger', text: 'Inactive' },
      'suspended': { color: 'warning', text: 'Paused' },
      'deactivated': { color: 'danger', text: 'Deactivated' },
      'pending': { color: 'warning', text: 'Pending' }
    };

    const config = statusConfig[userStatus] || { color: 'success', text: 'Active' };
    return <Badge color={config.color}>{config.text}</Badge>;
  };

  const getRoleBadge = (role) => {
    const roleColors = { 'admin': 'danger', 'user': 'primary' };
    return <Badge color={roleColors[role] || 'secondary'}>{role}</Badge>;
  };

  const handleExportAllUsers = (format) => {
    setExportLoading(true);

    dispatch(exportAdminUsers({
      search: searchTerm,
      status: statusFilter,
      callback: (exportData, error) => {
        setExportLoading(false);

        if (error) {
          console.error('Export failed:', error);
          return;
        }

        if (!exportData?.users) {
          console.error('No export data received');
          return;
        }

        // Use the exportFormattedData function with all users
        import('../../../utils/exportData').then(({ exportFormattedData }) => {
          const exportFilename = `all-users-${new Date().toISOString().split('T')[0]}`;

          // Prepare data with combined name field
          const preparedUsers = exportData.users.map(user => ({
            ...user,
            fullName: `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Unknown'
          }));

          exportFormattedData(
            preparedUsers,
            [
              { 'key': 'fullName', 'name': 'Name' },
              { 'key': 'email', 'name': 'Email' },
              { 'key': 'role', 'name': 'Role' },
              { 'key': 'statusText', 'name': 'Status' },
              { 'key': 'workspaceCount', 'name': 'Workspaces' },
              { 'key': 'workspaceNames', 'name': 'Workspace Names' },
              { 'key': 'lastLoginDate', 'name': 'Last Login' },
              { 'key': 'createdDate', 'name': 'Created' }
            ],
            format,
            `${exportFilename}.${format === 'excel' ? 'xlsx' : format}`
          );
        });
      }
    }));
  };

  return (
    <>
      <div className='content-dashboard'>
        {/* Show appropriate title based on mode */}
        {!isTabMode ? (
          <>
            <h3>{accessType === 'agency' ? 'Sub-Workspace Owners Management' : 'User Management'}</h3>
            <p className="text-muted mb-3">{accessType === 'agency' ? 'Manage owners of your sub-workspaces (clients)' : 'Manage all users across the platform'}</p>
          </>
        ) : (
          accessType === 'agency' && (
            <>
              <h4>Sub-Workspace Members</h4>
              {/* <p className="text-muted mb-3">Manage members across all your sub-workspaces</p> */}
            </>
          )
        )}


        {/* Statistics */}
        <div className='counter-sections'>
          <div className='counter-box style-two'>
            <div className='counter-start' >
              <div className='left-text-area'>
                <h5>Total Members</h5>
                <div className='CountUp-box'>
                  <h3 className="" style={{ color: '#2563EB' }}>
                    {adminUserStats['summary']?.['total'] || 0}
                  </h3>
                </div>
              </div>
              <div className='right-icon'>
                <span className='icon'><svg fill="#2563EB" width="20px" height="20px" viewBox="0 0 32 32" version="1.1" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0" /><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round" /><g id="SVGRepo_iconCarrier"> <title>users</title> <path d="M16 21.416c-5.035 0.022-9.243 3.537-10.326 8.247l-0.014 0.072c-0.018 0.080-0.029 0.172-0.029 0.266 0 0.69 0.56 1.25 1.25 1.25 0.596 0 1.095-0.418 1.22-0.976l0.002-0.008c0.825-3.658 4.047-6.35 7.897-6.35s7.073 2.692 7.887 6.297l0.010 0.054c0.127 0.566 0.625 0.982 1.221 0.982 0.69 0 1.25-0.559 1.25-1.25 0-0.095-0.011-0.187-0.031-0.276l0.002 0.008c-1.098-4.78-5.305-8.295-10.337-8.316h-0.002zM9.164 11.102c0 0 0 0 0 0 2.858 0 5.176-2.317 5.176-5.176s-2.317-5.176-5.176-5.176c-2.858 0-5.176 2.317-5.176 5.176v0c0.004 2.857 2.319 5.172 5.175 5.176h0zM9.164 3.25c0 0 0 0 0 0 1.478 0 2.676 1.198 2.676 2.676s-1.198 2.676-2.676 2.676c-1.478 0-2.676-1.198-2.676-2.676v0c0.002-1.477 1.199-2.674 2.676-2.676h0zM22.926 11.102c2.858 0 5.176-2.317 5.176-5.176s-2.317-5.176-5.176-5.176c-2.858 0-5.176 2.317-5.176 5.176v0c0.004 2.857 2.319 5.172 5.175 5.176h0zM22.926 3.25c1.478 0 2.676 1.198 2.676 2.676s-1.198 2.676-2.676 2.676c-1.478 0-2.676-1.198-2.676-2.676v0c0.002-1.477 1.199-2.674 2.676-2.676h0zM31.311 19.734c-0.864-4.111-4.46-7.154-8.767-7.154-0.395 0-0.784 0.026-1.165 0.075l0.045-0.005c-0.93-2.116-3.007-3.568-5.424-3.568-2.414 0-4.49 1.448-5.407 3.524l-0.015 0.038c-0.266-0.034-0.58-0.057-0.898-0.063l-0.009-0c-4.33 0.019-7.948 3.041-8.881 7.090l-0.012 0.062c-0.018 0.080-0.029 0.173-0.029 0.268 0 0.691 0.56 1.251 1.251 1.251 0.596 0 1.094-0.417 1.22-0.975l0.002-0.008c0.684-2.981 3.309-5.174 6.448-5.186h0.001c0.144 0 0.282 0.020 0.423 0.029 0.056 3.218 2.679 5.805 5.905 5.805 3.224 0 5.845-2.584 5.905-5.794l0-0.006c0.171-0.013 0.339-0.035 0.514-0.035 3.14 0.012 5.765 2.204 6.442 5.14l0.009 0.045c0.126 0.567 0.625 0.984 1.221 0.984 0.69 0 1.249-0.559 1.249-1.249 0-0.094-0.010-0.186-0.030-0.274l0.002 0.008zM16 18.416c-0 0-0 0-0.001 0-1.887 0-3.417-1.53-3.417-3.417s1.53-3.417 3.417-3.417c1.887 0 3.417 1.53 3.417 3.417 0 0 0 0 0 0.001v-0c-0.003 1.886-1.53 3.413-3.416 3.416h-0z" /> </g></svg></span>
              </div>
            </div>
          </div>

          <div className='counter-box style-two'>
            <div className='counter-start' >
              <div className='left-text-area'>
                <h5>Active Members</h5>
                <div className='CountUp-box'>
                  <h3 className="text-success">{adminUserStats['summary']?.['active'] || 0}</h3>
                </div>
              </div>
              <div className='right-icon'>
                <span className='icon'><svg fill="#16A34A" width="20px" height="20px" viewBox="0 0 32 32" version="1.1" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0" /><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round" /><g id="SVGRepo_iconCarrier"> <title>users</title> <path d="M16 21.416c-5.035 0.022-9.243 3.537-10.326 8.247l-0.014 0.072c-0.018 0.080-0.029 0.172-0.029 0.266 0 0.69 0.56 1.25 1.25 1.25 0.596 0 1.095-0.418 1.22-0.976l0.002-0.008c0.825-3.658 4.047-6.35 7.897-6.35s7.073 2.692 7.887 6.297l0.010 0.054c0.127 0.566 0.625 0.982 1.221 0.982 0.69 0 1.25-0.559 1.25-1.25 0-0.095-0.011-0.187-0.031-0.276l0.002 0.008c-1.098-4.78-5.305-8.295-10.337-8.316h-0.002zM9.164 11.102c0 0 0 0 0 0 2.858 0 5.176-2.317 5.176-5.176s-2.317-5.176-5.176-5.176c-2.858 0-5.176 2.317-5.176 5.176v0c0.004 2.857 2.319 5.172 5.175 5.176h0zM9.164 3.25c0 0 0 0 0 0 1.478 0 2.676 1.198 2.676 2.676s-1.198 2.676-2.676 2.676c-1.478 0-2.676-1.198-2.676-2.676v0c0.002-1.477 1.199-2.674 2.676-2.676h0zM22.926 11.102c2.858 0 5.176-2.317 5.176-5.176s-2.317-5.176-5.176-5.176c-2.858 0-5.176 2.317-5.176 5.176v0c0.004 2.857 2.319 5.172 5.175 5.176h0zM22.926 3.25c1.478 0 2.676 1.198 2.676 2.676s-1.198 2.676-2.676 2.676c-1.478 0-2.676-1.198-2.676-2.676v0c0.002-1.477 1.199-2.674 2.676-2.676h0zM31.311 19.734c-0.864-4.111-4.46-7.154-8.767-7.154-0.395 0-0.784 0.026-1.165 0.075l0.045-0.005c-0.93-2.116-3.007-3.568-5.424-3.568-2.414 0-4.49 1.448-5.407 3.524l-0.015 0.038c-0.266-0.034-0.58-0.057-0.898-0.063l-0.009-0c-4.33 0.019-7.948 3.041-8.881 7.090l-0.012 0.062c-0.018 0.080-0.029 0.173-0.029 0.268 0 0.691 0.56 1.251 1.251 1.251 0.596 0 1.094-0.417 1.22-0.975l0.002-0.008c0.684-2.981 3.309-5.174 6.448-5.186h0.001c0.144 0 0.282 0.020 0.423 0.029 0.056 3.218 2.679 5.805 5.905 5.805 3.224 0 5.845-2.584 5.905-5.794l0-0.006c0.171-0.013 0.339-0.035 0.514-0.035 3.14 0.012 5.765 2.204 6.442 5.14l0.009 0.045c0.126 0.567 0.625 0.984 1.221 0.984 0.69 0 1.249-0.559 1.249-1.249 0-0.094-0.010-0.186-0.030-0.274l0.002 0.008zM16 18.416c-0 0-0 0-0.001 0-1.887 0-3.417-1.53-3.417-3.417s1.53-3.417 3.417-3.417c1.887 0 3.417 1.53 3.417 3.417 0 0 0 0 0 0.001v-0c-0.003 1.886-1.53 3.413-3.416 3.416h-0z" /> </g></svg></span>
              </div>
            </div>
          </div>

          <div className='counter-box style-two'>
            <div className='counter-start' >
              <div className='left-text-area'>
                <h5>Paused Members</h5>
                <div className='CountUp-box'>
                  <h3 className="" style={{ color: '#EF4D56' }}>{adminUserStats['summary']?.['inactive'] || 0}</h3>
                </div>
              </div>
              <div className='right-icon'>
                <span className='icon'><svg fill="#EF4D56" width="20px" height="20px" viewBox="0 0 32 32" version="1.1" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0" /><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round" /><g id="SVGRepo_iconCarrier"> <title>users</title> <path d="M16 21.416c-5.035 0.022-9.243 3.537-10.326 8.247l-0.014 0.072c-0.018 0.080-0.029 0.172-0.029 0.266 0 0.69 0.56 1.25 1.25 1.25 0.596 0 1.095-0.418 1.22-0.976l0.002-0.008c0.825-3.658 4.047-6.35 7.897-6.35s7.073 2.692 7.887 6.297l0.010 0.054c0.127 0.566 0.625 0.982 1.221 0.982 0.69 0 1.25-0.559 1.25-1.25 0-0.095-0.011-0.187-0.031-0.276l0.002 0.008c-1.098-4.78-5.305-8.295-10.337-8.316h-0.002zM9.164 11.102c0 0 0 0 0 0 2.858 0 5.176-2.317 5.176-5.176s-2.317-5.176-5.176-5.176c-2.858 0-5.176 2.317-5.176 5.176v0c0.004 2.857 2.319 5.172 5.175 5.176h0zM9.164 3.25c0 0 0 0 0 0 1.478 0 2.676 1.198 2.676 2.676s-1.198 2.676-2.676 2.676c-1.478 0-2.676-1.198-2.676-2.676v0c0.002-1.477 1.199-2.674 2.676-2.676h0zM22.926 11.102c2.858 0 5.176-2.317 5.176-5.176s-2.317-5.176-5.176-5.176c-2.858 0-5.176 2.317-5.176 5.176v0c0.004 2.857 2.319 5.172 5.175 5.176h0zM22.926 3.25c1.478 0 2.676 1.198 2.676 2.676s-1.198 2.676-2.676 2.676c-1.478 0-2.676-1.198-2.676-2.676v0c0.002-1.477 1.199-2.674 2.676-2.676h0zM31.311 19.734c-0.864-4.111-4.46-7.154-8.767-7.154-0.395 0-0.784 0.026-1.165 0.075l0.045-0.005c-0.93-2.116-3.007-3.568-5.424-3.568-2.414 0-4.49 1.448-5.407 3.524l-0.015 0.038c-0.266-0.034-0.58-0.057-0.898-0.063l-0.009-0c-4.33 0.019-7.948 3.041-8.881 7.090l-0.012 0.062c-0.018 0.080-0.029 0.173-0.029 0.268 0 0.691 0.56 1.251 1.251 1.251 0.596 0 1.094-0.417 1.22-0.975l0.002-0.008c0.684-2.981 3.309-5.174 6.448-5.186h0.001c0.144 0 0.282 0.020 0.423 0.029 0.056 3.218 2.679 5.805 5.905 5.805 3.224 0 5.845-2.584 5.905-5.794l0-0.006c0.171-0.013 0.339-0.035 0.514-0.035 3.14 0.012 5.765 2.204 6.442 5.14l0.009 0.045c0.126 0.567 0.625 0.984 1.221 0.984 0.69 0 1.249-0.559 1.249-1.249 0-0.094-0.010-0.186-0.030-0.274l0.002 0.008zM16 18.416c-0 0-0 0-0.001 0-1.887 0-3.417-1.53-3.417-3.417s1.53-3.417 3.417-3.417c1.887 0 3.417 1.53 3.417 3.417 0 0 0 0 0 0.001v-0c-0.003 1.886-1.53 3.413-3.416 3.416h-0z" /> </g></svg></span>
              </div>
            </div>
          </div>

          <div className='counter-box style-two'>
            <div className='counter-start' >
              <div className='left-text-area'>
                <h5>Deactivated Members</h5>
                <div className='CountUp-box'>
                  <h3 className="" style={{ color: '#EF4444' }}>{adminUserStats['summary']?.['deactivated'] || 0}</h3>
                </div>
              </div>
              <div className='right-icon'>
                <span className='icon'><svg fill="#EF4444" width="20px" height="20px" viewBox="0 0 32 32" version="1.1" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0" /><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round" /><g id="SVGRepo_iconCarrier"> <title>user-slash</title> <path d="M28.707 3.293l-24 24c-0.391 0.391-0.391 1.024 0 1.414 0.195 0.195 0.451 0.293 0.707 0.293s0.512-0.098 0.707-0.293l24-24c0.391-0.391 0.391-1.024 0-1.414s-1.024-0.391-1.414 0zM16 17c-3.309 0-6-2.691-6-6 0-1.294 0.416-2.491 1.12-3.469l8.349 8.349c-0.978 0.704-2.175 1.12-3.469 1.12zM20.88 13.469l-8.349-8.349c0.978-0.704 2.175-1.12 3.469-1.12 3.309 0 6 2.691 6 6 0 1.294-0.416 2.491-1.12 3.469zM26 30h-20c-0.553 0-1-0.447-1-1s0.447-1 1-1h20c0.553 0 1 0.447 1 1s-0.447 1-1 1zM11.293 24c0.903-2.239 3.064-3.829 5.707-3.829s4.804 1.59 5.707 3.829h-11.414zM25.293 24c-1.226-3.037-4.163-5.829-8.293-5.829s-7.067 2.792-8.293 5.829h-2.707c0-4.418 3.582-8 8-8s8 3.582 8 8h2.293z" /> </g></svg></span>
              </div>
            </div>
          </div>

        </div>
        <div className='title-area'>
          <h4>All Members</h4>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button className='btn-style-border' onClick={handleCreateUserModalOpen}>+ Create Member</button>
          </div>
        </div>
        <div className="table-container filters-table">
          {/* Search and Filters */}
          <div className='header-table-filters advanced-table-filters'>
            <div className='left-area'>
              <div className='filter-box'>
                <div className='searchbox'>
                  <TextField
                    fullWidth
                    hiddenLabel
                    size="small"
                    name='search'
                    variant="standard"
                    placeholder="Search members..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <i className='icon'>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M5.82319 10.6464C3.16455 10.6464 1 8.48183 1 5.82319C1 3.16455 3.16455 1 5.82319 1C8.48183 1 10.6464 3.16455 10.6464 5.82319C10.6464 8.48183 8.48183 10.6464 5.82319 10.6464ZM5.82319 1.70583C3.55041 1.70583 1.70583 3.55511 1.70583 5.82319C1.70583 8.09127 3.55041 9.94055 5.82319 9.94055C8.09597 9.94055 9.94055 8.09127 9.94055 5.82319C9.94055 3.55511 8.09597 1.70583 5.82319 1.70583Z" fill="#505258" stroke="#505258" stroke-width="0.236842" />
                      <path d="M13.3182 13.7068C13.2245 13.7069 13.1307 13.6725 13.0566 13.5985L9.10425 9.63167C8.96097 9.48877 8.96065 9.25192 9.10355 9.10864C9.24646 8.96536 9.4833 8.96505 9.62658 9.10795L13.5789 13.0748C13.7222 13.2177 13.7225 13.4546 13.5796 13.5979C13.5057 13.672 13.412 13.7066 13.3182 13.7068Z" fill="#505258" stroke="#505258" stroke-width="0.236842" />
                    </svg>
                  </i>
                </div>
              </div>
              <div className='filter-box'>
                <Input
                  type="select"
                  id="status-filter"
                  name="statusFilter"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="">All Status</option>
                  <option value="active">Active</option>
                  <option value="suspended">Paused</option>
                  <option value="deactivated">Deactivated</option>
                </Input>
              </div>
            </div>
            <div className='right-deals-filtes'>
              <UncontrolledDropdown>
                <DropdownToggle
                  size="sm"
                  color="secondary"
                  caret
                  disabled={exportLoading}
                >
                  <i className="fa fa-download mr-1"></i>
                  {exportLoading ? 'Exporting...' : 'Export All Members'}
                </DropdownToggle>
                <DropdownMenu right>
                  <DropdownItem onClick={() => handleExportAllUsers('csv')}>
                    <i className="fa fa-file-text-o mr-2"></i>Export as CSV
                  </DropdownItem>
                  <DropdownItem onClick={() => handleExportAllUsers('excel')}>
                    <i className="fa fa-file-excel-o mr-2"></i>Export as Excel
                  </DropdownItem>
                  <DropdownItem onClick={() => handleExportAllUsers('pdf')}>
                    <i className="fa fa-file-pdf-o mr-2"></i>Export as PDF
                  </DropdownItem>
                  <DropdownItem divider />
                  <DropdownItem header>
                    <small className="text-muted">
                      Exports all {adminUserStats?.summary?.total || '0'} Members
                      {searchTerm && ` matching "${searchTerm}"`}
                      {statusFilter && ` (Status: ${statusFilter})`}
                    </small>
                  </DropdownItem>
                </DropdownMenu>
              </UncontrolledDropdown>
            </div>
          </div>
          {/* Users Table */}
          <div className='table-area'>
            {loading ? (
              <div className="admin-tab-loader">
                <div className="spinner-border text-primary" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
                <p className="text-muted">Loading members...</p>
              </div>
            ) : (
              <>
                <BulkActions
                  selectedItems={selectedUsers}
                  totalItems={adminUsers.length}
                  onSelectAll={handleSelectAll}
                  onDeselectAll={handleDeselectAll}
                  actions={bulkActions}
                  onAction={handleBulkAction}
                  isLoading={loading}
                />

                <Table responsive hover>
                  <thead>
                    <tr>
                      <th width="30">
                        <Checkbox checked={selectAll} onChange={handleSelectAll} />
                      </th>
                      <th>Members</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th>Workspaces</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {adminUsers.map(user => (
                      <tr key={user['_id']}>
                        <td>
                          <Checkbox checked={selectedUsers.includes(user['_id'])} onChange={() => handleUserSelect(user['_id'])} />
                        </td>
                        <td>
                          <div>
                            <strong>{user['firstName']} {user['lastName']}</strong>
                          </div>
                        </td>
                        <td>{user['email']}</td>
                        <td>{getRoleBadge(user['role'])}</td>
                        <td>{getUserStatusBadge(user['computedStatus'] || user['status'])}</td>
                        <td>
                          <Badge color="info">{user['workspaces']?.['length'] || 0}</Badge>
                        </td>
                        <td>{new Date(user['createdAt'])['toLocaleDateString']()}</td>
                        <td>
                          <div className='action-box'>
                            <button
                              className='action-btn'
                              color="secondary"
                              title="View Details"
                              onClick={() => {
                                setSelectedUser(user);
                                setShowSidebar(true);
                              }}
                            >
                              <i className="icon"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><circle opacity="0.1" cx="12" cy="12" r="12" fill="#323B43" /><path d="M12 14.165C10.805 14.165 9.83496 13.195 9.83496 12C9.83496 10.805 10.805 9.83501 12 9.83501C13.195 9.83501 14.165 10.805 14.165 12C14.165 13.195 13.195 14.165 12 14.165ZM12 10.585C11.22 10.585 10.585 11.22 10.585 12C10.585 12.78 11.22 13.415 12 13.415C12.78 13.415 13.415 12.78 13.415 12C13.415 11.22 12.78 10.585 12 10.585Z" fill="#323B43" /><path d="M12 16.51C10.12 16.51 8.34504 15.41 7.12504 13.5C6.59504 12.675 6.59504 11.33 7.12504 10.5C8.35004 8.58999 10.125 7.48999 12 7.48999C13.875 7.48999 15.65 8.58999 16.87 10.5C17.4 11.325 17.4 12.67 16.87 13.5C15.65 15.41 13.875 16.51 12 16.51ZM12 8.23999C10.385 8.23999 8.84004 9.20999 7.76004 10.905C7.38504 11.49 7.38504 12.51 7.76004 13.095C8.84004 14.79 10.385 15.76 12 15.76C13.615 15.76 15.16 14.79 16.24 13.095C16.615 12.51 16.615 11.49 16.24 10.905C15.16 9.20999 13.615 8.23999 12 8.23999Z" fill="#323B43" /></svg></i>
                            </button>

                            {(user?.['computedStatus'] || user?.['status']) !== 'deactivated' && (
                              <>
                                {(user?.['computedStatus'] || user?.['status']) === 'active' ? (
                                  <button
                                    className='action-btn suspend-btn'
                                    title="Pause User"
                                    onClick={() => handleUserAction(user['_id'], 'suspend')}
                                  >
                                    <i className="icon"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><circle opacity="0.1" cx="12" cy="12" r="12" fill="#FF8B00" /><path d="M10.32 16.875H8.505C7.575 16.875 7.125 16.445 7.125 15.555V8.445C7.125 7.555 7.575 7.125 8.505 7.125H10.32C11.25 7.125 11.7 7.555 11.7 8.445V15.555C11.7 16.445 11.25 16.875 10.32 16.875ZM8.505 7.875C7.965 7.875 7.875 8.01 7.875 8.445V15.555C7.875 15.99 7.96 16.125 8.505 16.125H10.32C10.86 16.125 10.95 15.99 10.95 15.555V8.445C10.95 8.01 10.865 7.875 10.32 7.875H8.505Z" fill="#FF8B00" /><path d="M15.4948 16.875H13.6798C12.7498 16.875 12.2998 16.445 12.2998 15.555V8.445C12.2998 7.555 12.7498 7.125 13.6798 7.125H15.4948C16.4248 7.125 16.8748 7.555 16.8748 8.445V15.555C16.8748 16.445 16.4248 16.875 15.4948 16.875ZM13.6798 7.875C13.1398 7.875 13.0498 8.01 13.0498 8.445V15.555C13.0498 15.99 13.1348 16.125 13.6798 16.125H15.4948C16.0348 16.125 16.1248 15.99 16.1248 15.555V8.445C16.1248 8.01 16.0398 7.875 15.4948 7.875H13.6798Z" fill="#FF8B00" /></svg></i>
                                  </button>
                                ) : (
                                  <button
                                    className='action-btn activate-btn'
                                    title="Activate User"
                                    onClick={() => handleUserAction(user['_id'], 'activate')}
                                  >
                                    <i className="icon"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><circle opacity="0.1" cx="12" cy="12" r="12" fill="#138B3F" /><path d="M10.1917 16.1708C9.82417 16.1708 9.48 16.0833 9.17667 15.9083C8.45917 15.4942 8.0625 14.66 8.0625 13.5633V10.4425C8.0625 9.34584 8.45917 8.51167 9.17667 8.09751C9.89417 7.68334 10.81 7.75917 11.7667 8.30751L14.4675 9.86501C15.4183 10.4133 15.9433 11.1717 15.9433 12C15.9433 12.8283 15.4183 13.5867 14.4675 14.135L11.7667 15.6925C11.2183 16.0133 10.6817 16.1708 10.1917 16.1708ZM10.1917 8.71001C9.97583 8.71001 9.7775 8.75667 9.60833 8.85584C9.17667 9.10667 8.9375 9.66667 8.9375 10.4425V13.5575C8.9375 14.3275 9.17667 14.8933 9.61417 15.1442C10.0517 15.395 10.6583 15.3192 11.3292 14.9342L14.03 13.3767C14.7008 12.9917 15.0683 12.5017 15.0683 12C15.0683 11.4983 14.7008 11.0083 14.03 10.6233L11.3292 9.06584C10.915 8.82667 10.53 8.71001 10.1917 8.71001Z" fill="#138B3F" /></svg></i>
                                  </button>
                                )}
                                <button
                                  className='action-btn impersonate-btn'
                                  title="Impersonate User"
                                  onClick={() => handleUserAction(user['_id'], 'impersonate')}
                                >
                                  <i className="icon"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><circle opacity="0.1" cx="12" cy="12" r="12" fill="#C73A63" /><path d="M14.4809 11.8077C14.9828 11.9323 15.467 12.121 15.9119 12.3951C16.1184 12.5214 16.3142 12.6656 16.51 12.8098C16.6577 12.9201 16.6666 13.0465 16.5474 13.1889C16.3587 13.4149 16.1665 13.6392 15.9742 13.8635C15.9689 13.8706 15.9671 13.8795 15.9582 13.8937C16.3658 14.1394 16.777 14.3797 17.1792 14.6324C17.3216 14.7214 17.4587 14.8246 17.5744 14.9457C17.8752 15.2572 18.0033 15.6416 18.0033 16.0706C18.0033 16.6081 18.0051 17.1456 18.0033 17.685C18.0033 17.9163 17.9215 18 17.6901 18C13.8989 18 10.1076 18 6.31465 18C6.08326 18 5.99961 17.9181 6.00139 17.685C6.00317 17.0869 5.99071 16.4871 6.0174 15.8908C6.04232 15.3568 6.31643 14.9492 6.76853 14.668C7.15477 14.4277 7.54813 14.1981 7.93615 13.9632C7.97353 13.9418 8.00913 13.9187 8.05362 13.8902C7.85783 13.6588 7.66738 13.4345 7.47693 13.2103C7.33988 13.0501 7.34522 12.9486 7.50719 12.8151C8.06964 12.3488 8.70507 12.0248 9.4117 11.8451C9.45263 11.8344 9.49535 11.8219 9.52917 11.813C9.46153 11.5407 9.39746 11.2737 9.32448 11.0085C9.31558 10.9747 9.26218 10.9444 9.22481 10.9302C8.81543 10.7896 8.43274 10.5973 8.12126 10.2894C7.72078 9.89606 7.70832 9.46888 8.08566 9.0506C8.37401 8.732 8.74245 8.52909 9.13937 8.38136C9.23371 8.34576 9.28532 8.29414 9.31914 8.20159C9.49891 7.71033 9.68046 7.21907 9.87269 6.73316C9.93143 6.58187 10.0097 6.43235 10.1023 6.29886C10.2874 6.03187 10.5277 5.94466 10.841 6.03365C11.019 6.08349 11.1898 6.16181 11.3589 6.23478C11.4817 6.28818 11.5992 6.35404 11.7185 6.41455C11.9107 6.51245 12.1011 6.50711 12.2934 6.41455C12.5034 6.31132 12.7117 6.20452 12.9235 6.10841C13.0178 6.06569 13.1193 6.03721 13.2207 6.01941C13.5002 5.96958 13.7333 6.06035 13.8828 6.30242C13.9932 6.48219 14.084 6.6762 14.1587 6.87199C14.326 7.30807 14.4809 7.74949 14.6357 8.18913C14.666 8.27634 14.7105 8.32796 14.8048 8.36178C15.1662 8.48993 15.5043 8.66614 15.7927 8.92245C15.8692 8.99187 15.9422 9.06662 16.0063 9.1485C16.2715 9.49024 16.2626 9.86224 15.9831 10.1915C15.6752 10.5546 15.264 10.7558 14.8244 10.9035C14.7123 10.9409 14.6749 10.9978 14.6535 11.1082C14.6055 11.3413 14.5396 11.5692 14.4809 11.8077ZM7.98065 13.0643C8.24051 13.3598 8.4897 13.6463 8.74245 13.9311C9.2426 14.4954 9.75166 15.0578 10.0667 15.7538C10.3106 16.2931 10.5241 16.8466 10.7449 17.3966C10.7822 17.4874 10.8214 17.5194 10.9193 17.5194C11.6384 17.5141 12.3592 17.5141 13.0783 17.5194C13.1851 17.5194 13.2225 17.4838 13.2599 17.3895C13.4735 16.8466 13.6835 16.302 13.9202 15.768C14.2281 15.072 14.739 14.5167 15.2356 13.956C15.4954 13.6624 15.7553 13.3669 16.0205 13.0643C15.5026 12.6674 14.9383 12.4075 14.3136 12.2847C14.2798 12.2776 14.2228 12.3167 14.1961 12.3506C14.0377 12.5392 13.8882 12.7368 13.7262 12.9201C13.6675 12.986 13.6336 13.0447 13.6354 13.1337C13.6408 13.4114 13.639 13.6891 13.6372 13.9667C13.6354 14.1323 13.5411 14.2355 13.4023 14.2373C13.2563 14.239 13.1602 14.134 13.1531 13.9649C13.1513 13.9169 13.1531 13.8688 13.1531 13.8225C13.1531 13.6766 13.1531 13.5324 13.1531 13.3687C12.7793 13.5627 12.4055 13.6641 12.0032 13.6641C11.5974 13.6624 11.2236 13.5502 10.8534 13.358C10.8534 13.5716 10.8534 13.762 10.8534 13.9525C10.8516 14.1394 10.7537 14.2586 10.606 14.2568C10.4618 14.2533 10.3675 14.1376 10.3675 13.9578C10.3657 13.6713 10.3711 13.3847 10.3639 13.0999C10.3622 13.0465 10.3337 12.986 10.3017 12.9433C10.145 12.7421 9.97771 12.5499 9.82464 12.3452C9.77836 12.2847 9.7392 12.2722 9.67334 12.2865C9.05749 12.42 8.49682 12.6727 7.98065 13.0643ZM11.8448 9.37633C11.6419 9.40481 11.2806 9.44218 10.9228 9.50982C10.6042 9.57034 10.2945 9.67535 10.0311 9.88004C9.84955 10.0207 9.75344 10.2004 9.75522 10.4389C9.76412 11.142 9.97593 11.7756 10.4013 12.3345C11.213 13.3989 12.5764 13.4666 13.4806 12.4841C13.8633 12.0693 14.0804 11.5674 14.1854 11.0174C14.2103 10.8857 14.2264 10.7522 14.2353 10.6187C14.2655 10.2058 14.2157 9.95658 13.6621 9.70027C13.1406 9.45286 12.5764 9.39591 11.8448 9.37633ZM6.50332 17.5105C7.75282 17.5105 9.00944 17.5105 10.2589 17.5105C9.91007 16.2681 9.31202 15.1735 8.36511 14.2746C7.90233 14.5505 7.43065 14.8246 6.96788 15.113C6.70979 15.275 6.52646 15.5206 6.51044 15.8232C6.48552 16.3803 6.50332 16.9392 6.50332 17.5105ZM17.521 17.5123C17.521 17.4642 17.521 17.4251 17.521 17.3877C17.521 16.941 17.5245 16.4942 17.5192 16.0492C17.5156 15.6274 17.3323 15.2999 16.9763 15.0738C16.8339 14.983 16.6862 14.8994 16.542 14.814C16.2377 14.6342 15.9333 14.4526 15.6378 14.28C14.6909 15.1806 14.0911 16.2717 13.744 17.5141C14.9953 17.5123 16.2483 17.5123 17.521 17.5123ZM14.7372 10.4283C15.0433 10.3215 15.3174 10.1773 15.5399 9.94946C15.7535 9.73231 15.7535 9.49202 15.5204 9.29445C15.3602 9.15918 15.1768 9.04348 14.9864 8.95449C14.2976 8.63232 13.5589 8.51663 12.8095 8.45789C12.0424 8.39738 11.277 8.41874 10.5152 8.53443C9.94033 8.62165 9.37432 8.7498 8.85992 9.03458C8.69617 9.12536 8.54666 9.24995 8.41317 9.38167C8.28857 9.50626 8.27433 9.67357 8.37223 9.82487C8.58226 10.1452 8.9151 10.2894 9.26396 10.4318C9.27464 10.3677 9.28354 10.325 9.28888 10.2841C9.32092 9.95836 9.47221 9.68959 9.73564 9.50982C9.95457 9.36031 10.1949 9.22503 10.444 9.14138C11.2824 8.85837 12.1421 8.83701 13.0089 8.99543C13.445 9.07552 13.8668 9.2019 14.2317 9.47066C14.4613 9.63975 14.6304 9.85512 14.6945 10.1381C14.7158 10.2289 14.723 10.3232 14.7372 10.4283ZM14.1142 8.16421C13.9291 7.66227 13.7689 7.19772 13.5838 6.74206C13.4806 6.48753 13.3239 6.44481 13.0676 6.56051C12.8629 6.65306 12.6636 6.75808 12.4607 6.85241C12.1563 6.99125 11.8484 6.99125 11.544 6.85241C11.3874 6.78122 11.2308 6.70468 11.0777 6.62636C10.6398 6.40387 10.4885 6.45905 10.3106 6.91649C10.1646 7.29561 10.0258 7.67651 9.88337 8.05741C9.87447 8.08233 9.87091 8.11081 9.86201 8.15709C11.2753 7.86162 12.6761 7.8634 14.1142 8.16421Z" fill="#C73A63" stroke="#C73A63" stroke-width="0.184615" /></svg></i>
                                </button>
                                {accessType === 'agency' && (
                                  <button
                                    className='action-btn text-danger'
                                    title="Deactivate User"
                                    onClick={() => handleUserAction(user['_id'], 'delete')}
                                  >
                                    <i className="fa fa-trash"></i>
                                  </button>
                                )}
                              </>
                            )}
                            {(user?.['computedStatus'] || user?.['status']) === 'deactivated' && (
                              <button
                                className='action-btn text-success'
                                title="Restore User"
                                onClick={() => handleUserAction(user['_id'], 'restore')}
                              >
                                <i className="fa fa-undo"></i>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>

                {/* Pagination */}
                <div className="d-flex justify-content-center mt-3">
                  <Pagination>
                    <PaginationItem disabled={currentPage === 1}>
                      <PaginationLink
                        previous
                        onClick={() => setCurrentPage(currentPage - 1)}
                      />
                    </PaginationItem>

                    {[...Array(Math.min(5, usersPagination?.pages || 1))].map((_, i) => {
                      const page = i + 1;
                      return (
                        <PaginationItem key={page} active={page === currentPage}>
                          <PaginationLink onClick={() => setCurrentPage(page)}>
                            {page}
                          </PaginationLink>
                        </PaginationItem>
                      );
                    })}

                    <PaginationItem disabled={currentPage === (usersPagination?.pages || 1)}>
                      <PaginationLink
                        next
                        onClick={() => setCurrentPage(currentPage + 1)}
                      />
                    </PaginationItem>
                  </Pagination>
                </div>
              </>
            )}
          </div>
        </div>


        {/* User Details Modal */}
        <Modal isOpen={showModal} toggle={() => setShowModal(false)} className={`main-modal new-user-modal create-property-modal modal-dialog-centered import-details-modal`}>
          <ModalHeader toggle={() => setShowModal(false)}>
            <h3>  Members Details</h3>
          </ModalHeader>
          <ModalBody>
            <Form className="dashboard-form ">
              {selectedUser && (
                <div>
                  <Row>
                    <Col md={6}>
                      <h5>{selectedUser['firstName']} {selectedUser['lastName']}</h5>
                      <p className="text-muted">{selectedUser['email']}</p>
                      <p>
                        Role: {getRoleBadge(selectedUser['role'])}&nbsp;
                        Status: {getUserStatusBadge(selectedUser['computedStatus'] || selectedUser['status'])}
                      </p>
                    </Col>
                    <Col md={6}>
                      <p><strong>Created:</strong> {new Date(selectedUser['createdAt'])['toLocaleString']()}</p>
                      <p><strong>Last Login:</strong> {selectedUser['lastLogin'] ? new Date(selectedUser['lastLogin'])['toLocaleString']() : 'Never'}</p>
                      <p><strong>Workspaces:</strong> {selectedUser['workspaces']?.['length'] || 0}</p>
                    </Col>
                  </Row>

                  {selectedUser['workspaces'] && selectedUser['workspaces']['length'] > 0 && (
                    <div className="mt-3">
                      <h6>Workspace Memberships</h6>
                      <div className="table-container filters-table mb-4">
                        <div className='table-area'>
                          <Table size="sm">
                            <thead>
                              <tr>
                                <th>Workspace</th>
                                <th>Role</th>
                                <th>Joined</th>
                              </tr>
                            </thead>
                            <tbody>
                              {selectedUser['workspaces'].map(ws => (
                                <tr key={ws['_id']}>
                                  <td>{ws['workspaceId']?.['name'] || 'Unknown'}</td>
                                  <td><Badge color="primary">{ws['role']}</Badge></td>
                                  <td>{new Date(ws['createdAt'])['toLocaleDateString']()}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Recent Activity for this user */}
                  <div className="mt-4">
                    <h6>Recent Activity</h6>
                    <div className="activity-list" style={{ maxHeight: '250px', overflowY: 'auto' }}>
                      {(activityLogs && activityLogs.length > 0 ? activityLogs : []).map(activity => (
                        <div key={activity['_id']} className="activity-item mb-3 pb-2 border-bottom">
                          <div className="d-flex align-items-start">
                            <div className="activity-icon">
                              <i className={`fa ${getActivityIcon(activity['type'])} ${getActivityColor(activity['severity'])}`}></i>
                            </div>
                            <div className="flex-grow-1">
                              <div className="activity-content">
                                <p className="mb-1 small">
                                  <strong>{activity['actor']?.['name'] || activity['actor']?.['email'] || 'User'}</strong> {activity['action']}
                                  {activity['workspace'] && (
                                    <span>&nbsp;in <strong>{activity['workspace']['name']}</strong></span>
                                  )}
                                </p>
                                <small className="text-muted">
                                  <i className="fa fa-clock mr-1"></i>
                                  {getRelativeTime(activity['timestamp'])}
                                  {activity['target']?.['type'] && (
                                    <span className="ml-2">
                                      <Badge color="secondary" className="badge-sm">{activity['target']['type']}</Badge>
                                    </span>
                                  )}
                                </small>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}

                      {(!activityLogs || activityLogs.length === 0) && (
                        <div className="text-center text-muted mt-2">
                          <i className="fa fa-clock fa-2x mb-2 d-block"></i>
                          <p>No recent activity</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </Form>
          </ModalBody>
          <ModalFooter>
            <button className='btn-style-new' onClick={() => setShowModal(false)}>
              Close
            </button>
          </ModalFooter>
        </Modal>

        {/* Create User Modal */}
        <Modal isOpen={showCreateModal} toggle={() => setShowCreateModal(false)} className={`main-modal new-user-modal create-property-modal modal-dialog-centered import-details-modal`}>
          <ModalHeader toggle={() => setShowCreateModal(false)}>
            <h3>Create New User</h3>
          </ModalHeader>
          <ModalBody>
            <Form className="dashboard-form mt-4" onSubmit={(e) => { e.preventDefault(); handleCreateUser(); }}>
              {/* Workspace Assignment - Always show for both admin and agency */}
              <div className="mb-4 p-3 border rounded bg-light">
                <FormGroup className="mb-3">
                  <h5><b>Workspace Assignment</b> <span className="text-danger">*</span></h5>
                  <div className="d-flex flex-column">
                    <div className="form-check mb-2 workspace-radio-group">
                      <FormControl sx={{paddingLeft: '10px'}}>
                        <RadioGroup
                          row
                          aria-labelledby="demo-row-radio-buttons-group-label"
                          name="row-radio-buttons-group"
                        >
                          <FormControlLabel value="new" control={<Radio name="workspaceOption" id="newWorkspace" checked={newUser.workspaceOption === 'new'} onChange={(e) => setNewUser({ ...newUser, workspaceOption: e.target.value, existingWorkspaceId: '' })} />} label="Create New Workspace" />
                          <FormHelperText>User will get their own workspace with immediate access</FormHelperText>
                          <FormControlLabel value="existing" control={<Radio name="workspaceOption" id="existingWorkspace" checked={newUser.workspaceOption === 'existing'}
                            onChange={(e) => setNewUser({ ...newUser, workspaceOption: e.target.value })}
                            disabled={!availableWorkspaces || availableWorkspaces.length === 0} />} label="Add to Existing Workspace" />
                          <FormHelperText>{availableWorkspaces && availableWorkspaces.length > 0
                            ? 'User will receive an invitation link via email'
                            : 'No workspaces available'}</FormHelperText>
                        </RadioGroup>
                      </FormControl>
                      {/* <input
                        className="form-check-input"
                        type="radio"
                        name="workspaceOption"
                        id="newWorkspace"
                        value="new"
                        checked={newUser.workspaceOption === 'new'}
                        onChange={(e) => setNewUser({ ...newUser, workspaceOption: e.target.value, existingWorkspaceId: '' })}
                      />
                      <label className="form-check-label" htmlFor="newWorkspace">
                        <strong>Create New Workspace</strong>
                        <small className="d-block text-muted">User will get their own workspace with immediate access</small>
                      </label> */}
                    </div>
                    {/* <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="workspaceOption"
                        id="existingWorkspace"
                        value="existing"
                        checked={newUser.workspaceOption === 'existing'}
                        onChange={(e) => setNewUser({ ...newUser, workspaceOption: e.target.value })}
                        disabled={!availableWorkspaces || availableWorkspaces.length === 0}
                      />
                      <label className="form-check-label" htmlFor="existingWorkspace">
                        <strong>Add to Existing Workspace</strong>
                        <small className="d-block text-muted">
                          {availableWorkspaces && availableWorkspaces.length > 0
                            ? 'User will receive an invitation link via email'
                            : 'No workspaces available'}
                        </small>
                      </label>
                    </div> */}
                  </div>
                </FormGroup>

                {newUser.workspaceOption === 'existing' && (
                  <FormGroup>
                    <Label for="existingWorkspaceId">Select Workspace <span className="text-danger">*</span></Label>
                    {workspacesLoading ? (
                      <div className="text-center py-2">
                        <small className="text-muted">Loading workspaces...</small>
                      </div>
                    ) : availableWorkspaces && availableWorkspaces.length > 0 ? (
                      <Input
                        type="select"
                        name="existingWorkspaceId"
                        id="existingWorkspaceId"
                        value={newUser['existingWorkspaceId']}
                        onChange={(e) => {
                          setNewUser({ ...newUser, 'existingWorkspaceId': e.target.value });
                          if (createErrors.existingWorkspaceId) setCreateErrors({ ...createErrors, existingWorkspaceId: '' });
                        }}
                        invalid={!!createErrors.existingWorkspaceId}
                        required
                      >
                        <option value="">Select a workspace...</option>
                        {availableWorkspaces.map(workspace => (
                          <option key={workspace._id} value={workspace._id}>
                            {workspace.name} ({workspace.memberCount} members)
                          </option>
                        ))}
                      </Input>
                    ) : (
                      <div className="alert alert-info mb-0">
                        <small>No workspaces available. Please create a workspace first or select "Create New Workspace" option.</small>
                      </div>
                    )}
                    {createErrors.existingWorkspaceId && <FormFeedback>{createErrors.existingWorkspaceId}</FormFeedback>}
                  </FormGroup>
                )}
              </div>

              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label for="firstName">First Name <span className="text-danger">*</span></Label>
                    <Input
                      type="text"
                      name="firstName"
                      id="firstName"
                      placeholder="Enter first name"
                      value={newUser['firstName']}
                      onChange={(e) => {
                        setNewUser({ ...newUser, 'firstName': e.target.value });
                        if (createErrors.firstName) setCreateErrors({ ...createErrors, firstName: '' });
                      }}
                      invalid={!!createErrors.firstName}
                      required
                    />
                    {createErrors.firstName && <FormFeedback>{createErrors.firstName}</FormFeedback>}
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label for="lastName">Last Name <span className="text-danger">*</span></Label>
                    <Input
                      type="text"
                      name="lastName"
                      id="lastName"
                      placeholder="Enter last name"
                      value={newUser['lastName']}
                      onChange={(e) => {
                        setNewUser({ ...newUser, 'lastName': e.target.value });
                        if (createErrors.lastName) setCreateErrors({ ...createErrors, lastName: '' });
                      }}
                      invalid={!!createErrors.lastName}
                      required
                    />
                    {createErrors.lastName && <FormFeedback>{createErrors.lastName}</FormFeedback>}
                  </FormGroup>
                </Col>
              </Row>

              <FormGroup>
                <Label for="email">Email Address <span className="text-danger">*</span></Label>
                <Input
                  type="email"
                  name="email"
                  id="email"
                  placeholder="user@example.com"
                  value={newUser['email']}
                  onChange={(e) => {
                    setNewUser({ ...newUser, 'email': e.target.value });
                    if (createErrors.email) setCreateErrors({ ...createErrors, email: '' });
                  }}
                  invalid={!!createErrors.email}
                  required
                />
                {createErrors.email && <FormFeedback>{createErrors.email}</FormFeedback>}
                <small className="text-muted">User will receive login credentials at this email</small>
              </FormGroup>

              {/* Only show password field for new workspace creation */}
              {newUser.workspaceOption === 'new' && (
                <FormGroup>
                  <Label for="password">Password <span className="text-danger">*</span></Label>
                  <Input
                    type="password"
                    name="password"
                    id="password"
                    placeholder="Minimum 6 characters"
                    value={newUser['password']}
                    onChange={(e) => {
                      setNewUser({ ...newUser, 'password': e.target.value });
                      if (createErrors.password) setCreateErrors({ ...createErrors, password: '' });
                    }}
                    invalid={!!createErrors.password}
                    minLength={6}
                    required
                  />
                  {createErrors.password && <FormFeedback>{createErrors.password}</FormFeedback>}
                  <small className="text-muted">Password must be at least 6 characters long</small>
                </FormGroup>
              )}

              {/* Show info message when adding to existing workspace */}
              {newUser.workspaceOption === 'existing' && (
                <Alert color="info" className="mb-3">
                  <strong>Note:</strong> User will receive an invitation link via email. No password is required as they will set it up during the invitation acceptance process.
                </Alert>
              )}

              <FormGroup>
                <Label for="status">Initial Status</Label>
                <Input
                  type="select"
                  name="status"
                  id="status"
                  value={newUser['status']}
                  onChange={(e) => setNewUser({ ...newUser, 'status': e.target.value })}
                >
                  <option value="active">Active - Immediate Access</option>
                  <option value="inactive">Inactive - No Access</option>
                </Input>
              </FormGroup>

              <div className="mt-3 p-3 bg-light rounded mb-3">
                <h6>What happens next?</h6>
                <ul className="mb-0 small">
                  {newUser.workspaceOption === 'new' ? (
                    <>
                      <li>User account will be created with the provided credentials</li>
                      <li>A personal workspace will be automatically created for the user</li>
                      <li>User will be set as owner of their own workspace</li>
                      {newUser.status === 'active' && <li>User can immediately log in with these credentials</li>}
                    </>
                  ) : (
                    <>
                      <li>User account will be created with the provided credentials</li>
                      <li>An invitation will be sent to the user's email</li>
                      <li>User must accept the invitation to join the selected workspace</li>
                      <li>User will be assigned the role you specified for that workspace</li>
                    </>
                  )}
                </ul>
              </div>
            </Form>
          </ModalBody>
          <ModalFooter>
            <button className='btn-style-border' onClick={() => {
              setShowCreateModal(false);
              setNewUser({ 'firstName': '', 'lastName': '', 'email': '', 'password': '', 'role': 'user', 'status': 'active', 'workspaceOption': 'new', 'existingWorkspaceId': '' });
              setCreateErrors({});
            }}>
              Cancel
            </button>
            <button className='btn-style-new' onClick={handleCreateUser} disabled={loading}>
              {loading ? 'Creating...' : 'Create User'}
            </button>
          </ModalFooter>
        </Modal>

        {/* User Details Sidebar */}
        <UserSidebar
          isOpen={showSidebar}
          onClose={() => setShowSidebar(false)}
          user={selectedUser}
        />

        {/* Confirmation Modal */}
        <Modal
          isOpen={showConfirmModal}
          toggle={() => setShowConfirmModal(false)}
          className="main-modal new-user-modal create-property-modal modal-dialog-centered import-details-modal"
          centered
          backdrop="static"
        >
          <ModalHeader toggle={() => setShowConfirmModal(false)}>
            <h3>
              <i className={`fa ${confirmModalConfig.icon} mr-2`} style={{ color: confirmModalConfig.iconColor }}></i>
              {confirmModalConfig.title}
            </h3>
          </ModalHeader>
          <ModalBody>
            <div className='title-area mt-4'>
              <i
                className={`fa ${confirmModalConfig.icon} mr-2`}
                style={{ color: confirmModalConfig.iconColor, opacity: 0.8 }}
              ></i>
              <h5>{confirmModalConfig.message}</h5>
            </div>
            {confirmModalConfig.showWarning && (
              <div className="alert alert-warning">
                <i className="fa fa-exclamation-triangle mr-2"></i>
                <strong>Important:</strong>
                <div style={{ whiteSpace: 'pre-line', marginTop: '10px', fontSize: '12px' }}>
                  {confirmModalConfig.warningMessage}
                </div>
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <button className='btn-style-border' onClick={() => setShowConfirmModal(false)}>
              {confirmModalConfig.cancelText}
            </button>
            <button className='btn-style-new' onClick={confirmModalConfig.onConfirm}>
              {confirmModalConfig.confirmText}
            </button>
          </ModalFooter>
        </Modal>

        {/* Info/Success Modal */}
        <Modal
          isOpen={showInfoModal}
          toggle={() => setShowInfoModal(false)}
          className="main-modal new-user-modal create-property-modal modal-dialog-centered info-modal"
          centered
          size="md"
        >
          <ModalBody>
            <div className='content-area'>
              <h4 className='mb-2'>
                <i
                  className={`fa ${infoModalConfig.icon} mr-2`}
                  style={{ color: infoModalConfig.iconColor }}
                ></i>
                {infoModalConfig.title}
              </h4>
              <p className='d-block w-100' style={{ whiteSpace: 'pre-line' }}>{infoModalConfig.message}</p>
            </div>
          </ModalBody>
        </Modal>

        {/* Custom Styles for Modals */}
        <style jsx>{`
          .confirmation-modal .modal-content {
            border-radius: 10px;
            box-shadow: 0 10px 40px rgba(0,0,0,0.2);
          }
          .info-modal .modal-content {
            border-radius: 10px;
            box-shadow: 0 10px 40px rgba(0,0,0,0.2);
            border: none;
          }
          .confirmation-modal .modal-header {
            border-bottom: 1px solid #e9ecef;
            padding: 1.25rem;
          }
          .confirmation-modal .modal-footer {
            border-top: 1px solid #e9ecef;thi
            padding: 1rem 1.25rem;
          }
          .info-modal .modal-body {
            padding: 2rem;
          }
        `}</style>
      </div>

    </>
  );
};

export default UsersManagement;